import { DateTime } from "luxon";
import { OAuthToken } from "@/services/oauth";
import { ResourceChannel } from "@/services/resources";
import { Resource } from "@/services/resources";
import { User } from "@/services/user";
import DOMPurify from "dompurify";
import { DeepReadonly } from "vue";
import { MARKETING_RESOURCE_CHANNELS } from "@/services/marketing";
import { SOCIAL_MEDIA_RESOURCE_CHANNELS } from "@/services/social_media";

export enum ResourceStatus {
  MISSING = "missing",
  ERROR = "error",
  ISSUE = "issue",
  OK = "ok",
}

export function tokenExpiresDate(
  token: DeepReadonly<OAuthToken> | undefined,
): DateTime | null {
  if (
    token &&
    (token.channel === ResourceChannel.FACEBOOK_ADS ||
      token.channel === ResourceChannel.FACEBOOK_ORGANIC ||
      token.channel === ResourceChannel.INSTAGRAM_ORGANIC)
  ) {
    return DateTime.fromISO(token.tokenExpiry as string);
  }
  if (
    token &&
    (token.channel === ResourceChannel.LINKED_IN_ADS ||
      token.channel === ResourceChannel.LINKED_IN_ORGANIC ||
      token.channel === ResourceChannel.TIK_TOK_ORGANIC)
  ) {
    return DateTime.fromISO(token.refreshTokenExpiry as string);
  }
  return null;
}

export function tokenExpiresInLessThanSevenDays(
  token: DeepReadonly<OAuthToken> | undefined,
): boolean {
  const date = tokenExpiresDate(token);
  if (!date) {
    return false;
  }
  return (
    date.minus({ days: 7 }).startOf("day") <= DateTime.utc().startOf("day")
  );
}
export function tokenHasExpired(
  token: DeepReadonly<OAuthToken> | undefined,
): boolean {
  const date = tokenExpiresDate(token);
  if (!date) {
    return false;
  }
  return date < DateTime.utc();
}

export function tokenExpiresDateString(
  token: DeepReadonly<OAuthToken> | undefined,
): string {
  const date = tokenExpiresDate(token);
  if (!date) {
    return "-";
  }
  return date.toLocaleString(DateTime.DATE_MED);
}

export function checkResourceStatus(
  resource: Resource,
  token: OAuthToken,
  user: User,
): ResourceStatus {
  if (token?.isRevoked || resource.isInaccessible || tokenHasExpired(token)) {
    return ResourceStatus.ERROR;
  } else if (
    user?.isActive === false ||
    tokenExpiresInLessThanSevenDays(token)
  ) {
    return ResourceStatus.ISSUE;
  } else {
    return ResourceStatus.OK;
  }
}

export function getSelectedResourceChannels(
  resources: Resource[],
): Set<ResourceChannel> {
  const channels = resources.map((r) => r.channel);
  return new Set(channels);
}

export function generateDataSourceText(resources: Resource[]): string {
  if (!resources?.length) return "";
  const n = resources.length;
  if (n && n === 1) {
    return `Data from ${resources?.[0].name}`;
  }
  let returnValue = "Data from: <ul>";
  resources.forEach((r) => {
    returnValue += `<li>${r.name} (${r.channel})</li>`;
  });
  returnValue += "</ul>";
  return DOMPurify.sanitize(returnValue);
}

export function isMarketingChannel(channel: ResourceChannel): boolean {
  return (MARKETING_RESOURCE_CHANNELS as unknown as ResourceChannel[]).includes(
    channel,
  );
}

// Custom-data resources carry a "pk-suffix" string id at runtime (e.g.
// "123-marketing"); everything else is a numeric pk.
export type SelectedResourceId = number | string;
export type CustomDataResourceCategory =
  | "marketing"
  | "socialMedia"
  | "customData";

// The singular form only parses. API and lookup boundaries should use the
// plural form, which drops malformed and non-positive ids.
export function normalizeSelectedResourceId(id: SelectedResourceId): number {
  return typeof id === "string" ? Number(id.split("-")[0]) : id;
}

export function normalizeSelectedResourceIds(
  ids: readonly SelectedResourceId[] | undefined,
): number[] {
  return [
    ...new Set(
      (ids ?? [])
        .map(normalizeSelectedResourceId)
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  ];
}

export function filterResourcesBySelectedIds(
  resources: readonly Resource[] | undefined,
  selectedResourceIds: readonly SelectedResourceId[] | undefined,
): Resource[] {
  const selectedIds = new Set(
    normalizeSelectedResourceIds(selectedResourceIds),
  );
  return (resources ?? []).filter((resource) => selectedIds.has(resource.pk));
}

export function getCustomDataResourceCategory(
  id: SelectedResourceId,
): CustomDataResourceCategory | undefined {
  if (typeof id !== "string") return undefined;

  const separatorIndex = id.lastIndexOf("-");
  if (separatorIndex === -1) return undefined;

  const category = id.slice(separatorIndex + 1);
  if (
    category === "marketing" ||
    category === "socialMedia" ||
    category === "customData"
  ) {
    return category;
  }
  return undefined;
}

export function selectionHasCustomDataCategory(
  ids: readonly SelectedResourceId[] | undefined,
  category: "marketing" | "socialMedia",
): boolean {
  return (ids ?? []).some(
    (id) => getCustomDataResourceCategory(id) === category,
  );
}

export function isAnalyticsChannel(
  channel: ResourceChannel,
  onlyGa4?: boolean,
): boolean {
  if (onlyGa4) return channel === ResourceChannel.GOOGLE_ANALYTICS_4;
  else {
    return (
      channel === ResourceChannel.GOOGLE_ANALYTICS ||
      channel === ResourceChannel.GOOGLE_ANALYTICS_4
    );
  }
}

export function isSocialMediaChannel(channel: ResourceChannel): boolean {
  return (
    SOCIAL_MEDIA_RESOURCE_CHANNELS as unknown as ResourceChannel[]
  ).includes(channel);
}
export function isSeoChannel(channel: ResourceChannel): boolean {
  return channel === ResourceChannel.GOOGLE_SEARCH_CONSOLE;
}

/**
 * Checks if a resource has an inactive token (requires refresh).
 * A token is considered inactive if it is:
 * - Revoked
 * - Expired (not just expiring soon)
 * - Or if the resource is inaccessible
 *
 * Note: Tokens expiring soon but still active will NOT trigger this warning.
 */
export function resourceHasInactiveToken(
  resource: Resource,
  token: OAuthToken | undefined,
): boolean {
  if (!token) return false;

  return resource.isInaccessible || token.isRevoked || tokenHasExpired(token);
}

/**
 * Gets the icon URL for a resource, with fallback to null if no custom icon is set.
 * Returns the custom icon URL if available, otherwise returns null (indicating
 * the caller should use the channel's default icon).
 *
 * @param resource - The resource to get the icon for
 * @returns The custom icon URL or null if not set
 */
export function getResourceIconUrl(
  resource: Resource | DeepReadonly<Resource> | undefined,
): string | null {
  return resource?.iconUrl || null;
}
